import type { InsightDrillTarget } from '../../../../../data/ServerApiClient'
import { cadenceCaption } from '../ceoCadenceModel'
import type { CeoCadence, CeoCadenceItem, CeoCockpitResponse } from '../ceoCockpitTypes'
import type { CeoSurface } from '../ceoCockpitUrlState'
import { CeoIncidentCard, CeoQuietCard, type CeoIncidentAction } from './CeoIncidentCard'

export type CeoCadenceViewProps = {
  payload: CeoCockpitResponse
  cadence: CeoCadence
  focusedKey?: string | null
  readOnly?: boolean
  onAction: (action: CeoIncidentAction, item: CeoCadenceItem) => void
  onShowNumbers: (item: CeoCadenceItem) => void
  onOpenDrill?: (target?: InsightDrillTarget | null) => void
  onCadenceChange: (cadence: CeoSurface) => void
}

const HINT: Record<CeoCadence, string> = {
  today: 'grouped from the signals the rules raised',
  week: 'weekly evidence, not one slow day',
  month: 'closed months only — nothing here is provisional',
}

const FOOT: Record<CeoCadence, string> = {
  today: 'Anything below the materiality floor, or that has not held for a second reading, is being held rather than shown.',
  week: 'Weekly items need several days of the same direction before they appear, so one quiet Tuesday never lands here.',
  month: 'Monthly items are read from closed books, so they change when a month closes, not when a day posts.',
}

const DOORS: Array<{ id: CeoSurface; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'explore', label: 'Explore the company' },
]

/**
 * One horizon's worth of findings — at most three, each with what it costs and
 * what to do next.
 *
 * The count is a cap, not a target. Most mornings have fewer, and a page that
 * always finds three teaches the reader that three is the weather rather than
 * a count worth reading.
 */
export function CeoCadenceView({
  payload,
  cadence,
  focusedKey,
  readOnly = false,
  onAction,
  onShowNumbers,
  onOpenDrill,
  onCadenceChange,
}: CeoCadenceViewProps) {
  const items = payload.cadences?.[cadence] ?? []
  const scope = payload.checked_scope
  const incidentsByKey = new Map((payload.incidents ?? []).map((inc) => [inc.incident_key, inc]))
  const since = payload.since_yesterday

  return (
    <div className="ceo-cadence-view">
      <section className="ceo-queue">
        <div className="ceo-strip-head">
          <span className="ceo-cap">{cadenceCaption(cadence, items)}</span>
          <span className="ceo-hint">{HINT[cadence]}</span>
        </div>
        {items.length > 0 ? (
          items.map((item, index) => (
            <CeoIncidentCard
              key={item.key}
              item={item}
              payload={payload}
              incident={incidentsByKey.get(item.key) ?? null}
              rank={index + 1}
              focused={focusedKey === item.key}
              readOnly={readOnly}
              onAction={onAction}
              onShowNumbers={onShowNumbers}
              onOpenDrill={onOpenDrill}
            />
          ))
        ) : (
          <CeoQuietCard
            areas={scope?.areas ?? 0}
            through={scope?.through ?? payload.as_of_date}
            cadence={cadence}
            alreadyShown={
              cadence === 'week'
                ? (payload.cadences?.week_already_shown ?? 0)
                : cadence === 'month'
                  ? (payload.cadences?.month_already_shown ?? 0)
                  : 0
            }
          />
        )}
      </section>

      <section className="ceo-since">
        {cadence === 'today' && since ? (
          <>
            <span className="ceo-cap">Since yesterday</span>
            <span className="ceo-since-line">
              {since.new} new · {since.worsening} worsening · {since.resolved} resolved
            </span>
          </>
        ) : (
          <>
            <span className="ceo-cap">Why only these</span>
            <span className="ceo-since-line">{FOOT[cadence]}</span>
          </>
        )}
        <span className="ceo-doors">
          {DOORS.filter((door) => door.id !== cadence).map((door) => (
            <button
              key={door.id}
              type="button"
              className="cfo-btn sm"
              onClick={() => onCadenceChange(door.id)}
            >
              {door.label} →
            </button>
          ))}
        </span>
      </section>
    </div>
  )
}
